import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { forkJoin, Subscription } from 'rxjs';
import { Constantes } from 'src/app/comun/data/constantes';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { MicroempresarioService } from 'src/app/microempresario/services/microempresario.service';
import { ParametrosService } from 'src/app/services/parametros.service';
import { SweetAlertService } from 'src/app/services/sweet-alert.service';
import {
  numeroDocumentoPorTipoDocValidator,
  TipoDocIdentidadParaValidacion,
} from 'src/app/shared/utils/form-validators.util';
import { SharedModule } from 'src/app/shared/shared.module';
import { HomeCuentaRegistroService } from '../../../services/home-cuenta-registro.service';
import { HomeCuentaCampaniaService } from '../../../services/home-cuenta-campania.service';
import {
  isCampaniaResponseOk,
  mapCampaniaNaturalExtras,
  mapCampaniaNaturalToHomeForm,
  resolveTipoDocOptionValue,
} from '../../../utils/home-cuenta-campania.mapper';
import { HomeCuentaFormFooterComponent } from '../home-cuenta-form-footer/home-cuenta-form-footer.component';
import {
  celularErrorMessage,
  correoErrorMessage,
  documentoErrorMessage,
  dominioCorreoBloqueadoValidator,
  HOME_EMAIL_PATTERN,
  HOME_PASSWORD_HINT,
  HOME_PASSWORD_PATTERN,
  isFieldInvalid,
  matchFields,
  passwordErrorMessage,
  requiredErrorMessage,
  atLeastOneSelected,
} from '../home-cuenta-form.validators';

declare var $: any;

@Component({
  selector: 'app-home-cuenta-form-natural',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    HomeCuentaFormFooterComponent,
  ],
  templateUrl: './home-cuenta-form-natural.component.html',
  styleUrls: ['./home-cuenta-form-natural.component.scss'],
})
export class HomeCuentaFormNaturalComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly idPrefix = 'home-cuenta-pn';
  readonly passwordHint = HOME_PASSWORD_HINT;
  submitting = false;
  showPassword = false;
  showConfirmPassword = false;
  /** Política de contraseña solo mientras se diligencia el campo. */
  passwordFieldActive = false;
  /** True cuando hay mensajes de error visibles (para expandir el panel). */
  hasVisibleErrors = false;
  private campaniaPrefillDone = false;
  private documentosListos = false;
  private pendingCampaniaPatch: ReturnType<
    typeof mapCampaniaNaturalToHomeForm
  > = null;

  @Output() readonly visibleErrorsChange = new EventEmitter<boolean>();

  documentos: Array<TipoDocIdentidadParaValidacion & { nombre?: string }> = [];
  tiposContacto: Array<{ idTipoContacto: number; nombre: string }> = [];
  /** Opciones visibles en el select (filtradas por RUT). */
  fuentes: Array<{ idFuenteNeocredito: number; fuente: string }> = [];
  /** Caché formal/informal: se cargan una vez al iniciar. */
  private fuentesFormal: Array<{ idFuenteNeocredito: number; fuente: string }> = [];
  private fuentesInformal: Array<{ idFuenteNeocredito: number; fuente: string }> = [];
  private fuentesListas = false;
  /**
   * Campaña BX: la procedencia se elige al crear la campaña sin distinguir
   * formal/informal → el select usa el catálogo combinado.
   */
  private procedenciasSinFiltroRut = false;
  private dominiosCorreoBloqueados: string[] = [];
  private readonly subs = new Subscription();
  private footerChecksSub?: Subscription;

  /** Procedencia habilitada tras RUT, o en campaña con fuente precargada. */
  get origenHabilitado(): boolean {
    return (
      Boolean(this.form.get('tieneRUT')?.value) || this.procedenciasSinFiltroRut
    );
  }

  readonly form = this.fb.nonNullable.group(
    {
      primerNombre: ['', [Validators.required, Validators.maxLength(20)]],
      segundoNombre: ['', Validators.maxLength(20)],
      primerApellido: ['', [Validators.required, Validators.maxLength(20)]],
      segundoApellido: ['', Validators.maxLength(20)],
      celular: [
        '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(10),
          Validators.pattern(/^\d{10}$/),
        ],
      ],
      correo: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(HOME_EMAIL_PATTERN),
          dominioCorreoBloqueadoValidator(() => this.dominiosCorreoBloqueados),
        ],
      ],
      tipoDoc: ['', Validators.required],
      numDoc: [
        '',
        [
          Validators.required,
          Validators.maxLength(15),
          numeroDocumentoPorTipoDocValidator(() => this.documentos, 'tipoDoc'),
        ],
      ],
      confirmaDoc: [
        '',
        [
          Validators.required,
          Validators.maxLength(15),
          numeroDocumentoPorTipoDocValidator(() => this.documentos, 'tipoDoc'),
        ],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(HOME_PASSWORD_PATTERN),
        ],
      ],
      confirmaPassword: ['', Validators.required],
      contacto: [[] as Array<string | number>, atLeastOneSelected()],
      tieneRUT: ['', Validators.required],
      origen: ['', Validators.required],
    },
    {
      validators: [
        matchFields('numDoc', 'confirmaDoc', 'confirmDocMismatch'),
        matchFields('password', 'confirmaPassword', 'confirmPasswordMismatch'),
      ],
    }
  );

  @ViewChild(HomeCuentaFormFooterComponent)
  private footer?: HomeCuentaFormFooterComponent;

  constructor(
    private readonly fb: FormBuilder,
    private readonly parametros: ParametrosService,
    private readonly microempresario: MicroempresarioService,
    private readonly registro: HomeCuentaRegistroService,
    private readonly sweetAlert: SweetAlertService,
    private readonly bancoldex: BancoldexService,
    private readonly campania: HomeCuentaCampaniaService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarCatalogos();
    this.cargarDominiosCorreoBloqueados();
    this.subs.add(
      this.form.get('tipoDoc')!.valueChanges.subscribe(() => {
        this.form.get('numDoc')?.setValue('', { emitEvent: false });
        this.form.get('confirmaDoc')?.setValue('', { emitEvent: false });
        this.form.get('numDoc')?.markAsPristine();
        this.form.get('numDoc')?.markAsUntouched();
        this.form.get('confirmaDoc')?.markAsPristine();
        this.form.get('confirmaDoc')?.markAsUntouched();
        this.form.get('numDoc')?.updateValueAndValidity({ emitEvent: false });
        this.form.get('confirmaDoc')?.updateValueAndValidity({ emitEvent: false });
        this.syncVisibleErrors();
      })
    );
    this.subs.add(
      this.form.get('tieneRUT')!.valueChanges.subscribe((valor: string) => {
        // Campaña: procedencia de BX no depende del RUT → no limpiar ni filtrar.
        if (this.procedenciasSinFiltroRut) {
          this.refreshOrigenSelectpicker();
          this.syncVisibleErrors();
          return;
        }
        this.form.get('origen')?.setValue('', { emitEvent: false });
        this.aplicarFuentesPorRut(valor);
        this.syncVisibleErrors();
      })
    );
    this.subs.add(
      this.form.statusChanges.subscribe(() => this.syncVisibleErrors())
    );
    this.subs.add(
      this.campania.params$.subscribe((params) => {
        if (params?.perfil === 'natural' && !this.campaniaPrefillDone) {
          this.cargarPrefillCampania(params.idCampania, params.token);
        }
      })
    );
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.bindFooterChecks();
      this.refreshOrigenSelectpicker();
      this.refreshContactoSelectpicker();
    });
  }

  ngOnDestroy(): void {
    this.destroyOrigenSelectpicker();
    this.destroyContactoSelectpicker();
    this.footerChecksSub?.unsubscribe();
    this.subs.unsubscribe();
  }

  @HostListener('focusout')
  onFocusOut(): void {
    queueMicrotask(() => this.syncVisibleErrors());
  }

  isInvalid(key: string): boolean {
    return isFieldInvalid(this.form, key);
  }

  errorMsg(key: string): string {
    const c = this.form.get(key);
    switch (key) {
      case 'correo':
        return correoErrorMessage(c);
      case 'celular':
        return celularErrorMessage(c);
      case 'numDoc':
      case 'confirmaDoc':
        return documentoErrorMessage(c);
      case 'password':
      case 'confirmaPassword':
        return passwordErrorMessage(c);
      default:
        return requiredErrorMessage(c);
    }
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onPasswordFocus(): void {
    this.passwordFieldActive = true;
  }

  onPasswordBlur(): void {
    this.passwordFieldActive = false;
  }

  onCorreoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const lower = input.value.toLowerCase();
    if (input.value !== lower) {
      this.form.get('correo')?.setValue(lower, { emitEvent: false });
    }
  }

  /** Quita marcas de error/touched sin borrar valores (cambio de tab). */
  clearValidationState(): void {
    this.passwordFieldActive = false;
    this.form.markAsPristine();
    this.form.markAsUntouched();
    Object.values(this.form.controls).forEach((control) => {
      control.markAsPristine();
      control.markAsUntouched();
    });
    this.footer?.clearValidationState();
    this.syncVisibleErrors();
    this.refreshOrigenSelectpicker();
    this.refreshContactoSelectpicker();
  }

  /**
   * Equivalente a `selectpikerChangeContactos` del registro legacy.
   */
  selectpickerChangeContactos(): void {
    const fieldId = `${this.idPrefix}-contacto`;
    const $el = $(`#${fieldId}`);
    const tiposContactoElement = document.getElementById(fieldId);
    if (!tiposContactoElement || !$el?.length) {
      return;
    }

    this.form.get('contacto')?.markAsDirty();
    this.form.get('contacto')?.markAsTouched();
    this.syncVisibleErrors();
    this.cdr.detectChanges();
    this.syncSelectpickerInvalidClass('contacto');
    this.styleSelectpickerDropdown($el, 'home-cuenta-contacto-dropdown');
  }

  /** Limpia/aplica borde rojo del selectpicker de procedencia al elegir opción. */
  selectpickerChangeOrigen(): void {
    const $el = $(`#${this.idPrefix}-origen`);
    if (!$el?.length) {
      return;
    }

    this.form.get('origen')?.markAsDirty();
    this.form.get('origen')?.markAsTouched();
    this.syncVisibleErrors();
    this.cdr.detectChanges();
    this.syncSelectpickerInvalidClass('origen');
    this.styleSelectpickerDropdown($el, 'home-cuenta-origen-dropdown');
  }

  /**
   * Alinea is-invalid del <select>, wrapper bootstrap-select y botón.
   * No toca el FormControl (formControlName ya sincroniza el valor).
   */
  private syncSelectpickerInvalidClass(controlName: 'origen' | 'contacto'): void {
    const fieldId = `${this.idPrefix}-${controlName}`;
    const $el = $(`#${fieldId}`);
    const el = document.getElementById(fieldId);
    if (!el || !$el?.length) {
      return;
    }
    const invalid = this.isInvalid(controlName);
    const $wrap = $el.parent().hasClass('bootstrap-select') ? $el.parent() : null;
    const $btn = $wrap?.find('> .dropdown-toggle');
    if (invalid) {
      el.classList.add('is-invalid');
      $wrap?.addClass('is-invalid');
      $btn?.addClass('is-invalid');
    } else {
      el.classList.remove('is-invalid');
      $wrap?.removeClass('is-invalid');
      $btn?.removeClass('is-invalid');
    }
  }

  onSubmit(): void {
    if (this.form.invalid || !this.footer?.canSubmit || this.submitting) {
      this.form.markAllAsTouched();
      this.footer?.checksForm.markAllAsTouched();
      this.syncVisibleErrors();
      this.refreshOrigenSelectpicker();
      this.refreshContactoSelectpicker();
      this.syncSelectpickerInvalidClass('origen');
      this.syncSelectpickerInvalidClass('contacto');
      return;
    }

    this.submitting = true;
    const tratamientoDatos = Boolean(this.footer.checksForm.get('datos')?.value);
    this.registro.registrarNatural(this.form.getRawValue(), tratamientoDatos).subscribe({
      next: () => {
        this.submitting = false;
      },
      error: (err) => {
        this.submitting = false;
        this.sweetAlert.errorModal(
          'Error!',
          true,
          err?.error || err?.message || 'No fue posible completar el registro'
        );
      },
    });
  }

  private bindFooterChecks(): void {
    this.footerChecksSub?.unsubscribe();
    if (!this.footer) {
      return;
    }
    this.footerChecksSub = this.footer.checksForm.statusChanges.subscribe(() =>
      this.syncVisibleErrors()
    );
  }

  private syncVisibleErrors(): void {
    const formHas = Object.values(this.form.controls).some(
      (control) => (control.touched || control.dirty) && control.invalid
    );
    const footerHas = this.footer
      ? Object.values(this.footer.checksForm.controls).some(
          (control) => (control.touched || control.dirty) && control.invalid
        )
      : false;
    const next = formHas || footerHas;
    if (next === this.hasVisibleErrors) {
      return;
    }
    this.hasVisibleErrors = next;
    this.visibleErrorsChange.emit(next);
  }

  private cargarCatalogos(): void {
    this.parametros.getTipoDocumentos().subscribe((data: any) => {
      if (!data) {
        return;
      }
      this.documentos = data.filter(
        (documento: any) =>
          documento.personaJuridica == 0 &&
          documento.idTipoDocIdentidad != 4 &&
          documento.idTipoDocIdentidad != 6
      );
      this.documentosListos = true;
      this.form.get('numDoc')?.updateValueAndValidity({ emitEvent: false });
      this.form.get('confirmaDoc')?.updateValueAndValidity({ emitEvent: false });
      this.aplicarPrefillCampaniaPendiente();
    });

    this.microempresario.getListaTipoContacto().subscribe((data: any) => {
      this.tiposContacto = Array.isArray(data) ? data : data?.dtos || [];
      setTimeout(() => this.refreshContactoSelectpicker(), 0);
    });

    this.cargarFuentesCache();
  }

  /** Carga formal e informal una sola vez; el cambio de RUT solo filtra en cliente. */
  private cargarFuentesCache(): void {
    forkJoin({
      formal: this.microempresario.getFuentes({
        tiposPersona: [
          { idTipoPersonaJuridica: Constantes.TIPO_PERSONA.NATURAL_FORMAL },
        ],
      }),
      informal: this.microempresario.getFuentes({
        tiposPersona: [
          { idTipoPersonaJuridica: Constantes.TIPO_PERSONA.NATURAL_INFORMAL },
        ],
      }),
    }).subscribe({
      next: ({ formal, informal }: { formal: any; informal: any }) => {
        if (formal?.exitoso === false || informal?.exitoso === false) {
          this.sweetAlert.errorModal(
            'Error',
            true,
            'Error al cargar las fuentes de información'
          );
          return;
        }
        this.fuentesFormal = formal?.fuentes || formal?.dtos || [];
        this.fuentesInformal = informal?.fuentes || informal?.dtos || [];
        this.fuentesListas = true;
        const rut = this.form.get('tieneRUT')?.value;
        if (rut) {
          this.aplicarFuentesPorRut(rut);
        }
        this.aplicarPrefillCampaniaPendiente();
      },
      error: () => {
        this.sweetAlert.errorModal(
          'Error!',
          true,
          'Error al cargar las fuentes de información'
        );
      },
    });
  }

  /** Filtra el select de procedencia desde el caché (sin llamar al backend). */
  private aplicarFuentesPorRut(valor: string, afterApply?: () => void): void {
    if (!valor) {
      this.fuentes = [];
    } else if (valor === Constantes.VALOR_SI) {
      this.fuentes = [...this.fuentesFormal];
    } else {
      this.fuentes = [...this.fuentesInformal];
    }
    this.cdr.detectChanges();
    setTimeout(() => {
      this.refreshOrigenSelectpicker();
      afterApply?.();
    }, 0);
  }

  /**
   * Catálogo formal + informal (sin distinción RUT).
   * Usado en precarga de campaña: la fuente se eligió al crear la campaña.
   */
  private aplicarFuentesCombinadas(afterApply?: () => void): void {
    const seen = new Set<number>();
    const merged: Array<{ idFuenteNeocredito: number; fuente: string }> = [];
    for (const f of [...this.fuentesFormal, ...this.fuentesInformal]) {
      const id = Number(f.idFuenteNeocredito);
      if (!Number.isFinite(id) || seen.has(id)) {
        continue;
      }
      seen.add(id);
      merged.push(f);
    }
    this.fuentes = merged;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.refreshOrigenSelectpicker();
      afterApply?.();
    }, 0);
  }

  private cargarPrefillCampania(idCampania: string, token: string): void {
    this.campaniaPrefillDone = true;
    this.microempresario
      .cargarRegistroNatural({ idCampania, token })
      .subscribe((response: any) => {
        if (!isCampaniaResponseOk(response)) {
          this.campania.clear();
          this.sweetAlert.errorModal(
            'Error!',
            true,
            response?.error ||
              response?.mensaje ||
              'No se encontró la campaña'
          );
          return;
        }
        const patch = mapCampaniaNaturalToHomeForm(response?.data);
        if (!patch) {
          this.campania.clear();
          return;
        }
        this.campania.setExtrasNatural(
          mapCampaniaNaturalExtras(response?.data)
        );
        this.pendingCampaniaPatch = patch;
        this.aplicarPrefillCampaniaPendiente();
      });
  }

  private aplicarPrefillCampaniaPendiente(): void {
    const patch = this.pendingCampaniaPatch;
    if (!patch || !this.documentosListos || !this.fuentesListas) {
      return;
    }
    this.pendingCampaniaPatch = null;
    this.campania.clear();

    const tipoDoc = resolveTipoDocOptionValue(this.documentos, patch.tipoDoc);

    // Resto de campos primero (sin tipoDoc), como en legacy no se “resetea” el select.
    this.form.patchValue(
      {
        primerNombre: patch.primerNombre,
        segundoNombre: patch.segundoNombre,
        primerApellido: patch.primerApellido,
        segundoApellido: patch.segundoApellido,
        celular: patch.celular,
        correo: patch.correo,
        numDoc: patch.numDoc,
        confirmaDoc: patch.confirmaDoc,
      },
      { emitEvent: false }
    );
    this.cdr.detectChanges();

    // Mismo patrón que persona-natural.ts loadFields: setValue del id (string del catálogo).
    setTimeout(() => {
      if (tipoDoc !== '' && tipoDoc != null) {
        this.form.get('tipoDoc')?.setValue(String(tipoDoc), { emitEvent: false });
      }
      this.form.get('numDoc')?.setValue(patch.numDoc, { emitEvent: false });
      this.form.get('confirmaDoc')?.setValue(patch.confirmaDoc, {
        emitEvent: false,
      });

      const tieneRut = patch.tieneRUT?.trim() || '';
      const origenCampania = patch.origen?.trim() || '';

      // Campaña: procedencia elegida en BX sin formal/informal → catálogo combinado.
      if (origenCampania) {
        this.procedenciasSinFiltroRut = true;
      }

      if (tieneRut) {
        this.form.get('tieneRUT')?.setValue(tieneRut, { emitEvent: false });
      }

      const aplicarOrigen = (): void => {
        if (origenCampania) {
          this.form.get('origen')?.setValue(origenCampania, { emitEvent: false });
          this.refreshOrigenSelectpicker();
        }
      };

      if (this.procedenciasSinFiltroRut) {
        this.aplicarFuentesCombinadas(aplicarOrigen);
      } else if (tieneRut) {
        this.aplicarFuentesPorRut(tieneRut, aplicarOrigen);
      } else {
        this.refreshOrigenSelectpicker();
      }

      this.refreshContactoSelectpicker();
      this.cdr.detectChanges();
    }, 100);
  }

  private refreshOrigenSelectpicker(): void {
    const $el = $(`#${this.idPrefix}-origen`);
    if (!$el?.length || typeof $el.selectpicker !== 'function') {
      return;
    }
    if ($el.parent().hasClass('bootstrap-select')) {
      $el.selectpicker('destroy');
    }
    $el.prop('disabled', !this.origenHabilitado);
    if (this.isInvalid('origen')) {
      $el.addClass('is-invalid');
    } else {
      $el.removeClass('is-invalid');
    }
    $el.addClass('selectpicker');
    $el.selectpicker({
      container: 'body',
      liveSearch: true,
      size: 6,
      width: '100%',
      dropupAuto: true,
      noneSelectedText: 'Selecciona una opción',
    });
    const origen = this.form.get('origen')?.value;
    if (origen != null && String(origen).length > 0) {
      $el.selectpicker('val', String(origen));
    }
    $el
      .off('shown.bs.select.homeCuentaOrigen changed.bs.select.homeCuentaOrigen')
      .on('shown.bs.select.homeCuentaOrigen', () =>
        this.styleSelectpickerDropdown($el, 'home-cuenta-origen-dropdown')
      )
      .on('changed.bs.select.homeCuentaOrigen', () => this.selectpickerChangeOrigen());
    this.styleSelectpickerDropdown($el, 'home-cuenta-origen-dropdown');
    this.syncSelectpickerInvalidClass('origen');
  }

  private normalizeContactoIds(value: unknown): Array<number> {
    const raw: unknown[] = Array.isArray(value)
      ? value
      : value != null && String(value).length > 0
        ? [value]
        : [];
    return raw
      .map((item) => Number(item))
      .filter((n) => !Number.isNaN(n) && n > 0);
  }

  private refreshContactoSelectpicker(): void {
    const $el = $(`#${this.idPrefix}-contacto`);
    if (!$el?.length || typeof $el.selectpicker !== 'function') {
      return;
    }
    const currentValue = this.form.get('contacto')?.value;

    if ($el.parent().hasClass('bootstrap-select')) {
      $el.selectpicker('destroy');
    }
    if (this.isInvalid('contacto')) {
      $el.addClass('is-invalid');
    } else {
      $el.removeClass('is-invalid');
    }
    $el.addClass('selectpicker');
    $el.selectpicker({
      container: 'body',
      liveSearch: true,
      size: 4,
      width: '100%',
      dropupAuto: true,
      actionsBox: true,
      selectAllText: "<i class='fas fa-plus'></i> Seleccionar",
      deselectAllText: "<i class='fas fa-minus'></i> Quitar todos",
      title: 'Selecciona una opción',
      noneSelectedText: 'Selecciona una opción',
      selectedTextFormat: 'count > 2',
      countSelectedText: '{0} seleccionados',
    });

    // Restaura selección visual desde el FormControl (fuente de verdad = Angular)
    const selectedIds = this.normalizeContactoIds(currentValue);
    if (selectedIds.length > 0) {
      $el.selectpicker('val', selectedIds.map(String));
    }

    $el
      .off('changed.bs.select.homeCuentaContacto shown.bs.select.homeCuentaContacto')
      .on('shown.bs.select.homeCuentaContacto', () =>
        this.styleSelectpickerDropdown($el, 'home-cuenta-contacto-dropdown')
      )
      .on('changed.bs.select.homeCuentaContacto', () =>
        this.selectpickerChangeContactos()
      );
    this.styleSelectpickerDropdown($el, 'home-cuenta-contacto-dropdown');
    this.syncSelectpickerInvalidClass('contacto');
  }

  private styleSelectpickerDropdown($el: JQuery | any, menuClass: string): void {
    const picker = $el.data('selectpicker');
    if (!picker?.$menu?.length) {
      return;
    }
    const btnWidth = picker.$newElement?.outerWidth?.() || $el.outerWidth();
    const $menu = picker.$menu;
    $menu.addClass(menuClass);
    if (btnWidth) {
      $menu[0].style.setProperty('--home-cuenta-origen-width', `${btnWidth}px`);
      $menu[0].style.setProperty('overflow-x', 'hidden', 'important');
    }
    $menu.find('.inner').css({ overflowX: 'hidden', maxWidth: '100%' });
    $menu.find('li a, li a span.text').css({
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
    });
    // Por si queda un bs-title-option residual tras destroy/reinit
    $menu.find('a.bs-title-option').closest('li').hide();
  }

  private destroyOrigenSelectpicker(): void {
    const $el = $(`#${this.idPrefix}-origen`);
    $el.off('shown.bs.select.homeCuentaOrigen changed.bs.select.homeCuentaOrigen');
    if ($el?.length && $el.parent().hasClass('bootstrap-select')) {
      $el.selectpicker('destroy');
    }
  }

  private destroyContactoSelectpicker(): void {
    const $el = $(`#${this.idPrefix}-contacto`);
    $el.off('shown.bs.select.homeCuentaContacto');
    if ($el?.length && $el.parent().hasClass('bootstrap-select')) {
      $el.selectpicker('destroy');
    }
  }

  private cargarDominiosCorreoBloqueados(): void {
    this.bancoldex
      .getFilterParametro({
        idParametro: 'PAR-160',
        nombre: null,
        isParFuncional: true,
        pagina: 1,
      })
      .subscribe({
        next: (response: any) => {
          const valor = response?.data?.[0]?.valorParametro;
          if (typeof valor === 'string' && valor.trim()) {
            this.dominiosCorreoBloqueados = valor
              .split(',')
              .map((d: string) => d.trim().toLowerCase())
              .filter((d: string) => d.length > 0);
          }
          this.form.get('correo')?.updateValueAndValidity({ emitEvent: false });
        },
        error: () => {
          // Sin PAR-160 se valida solo formato
        },
      });
  }
}
